'use client'

import React, { useRef, useEffect, useState } from 'react'
import Link from 'next/link'
import { usePrivy } from '@privy-io/react-auth'
import { useRouter } from 'next/navigation'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { LaserFlow } from '@/components/laser-flow'
import { ChevronRight } from 'lucide-react'
import { InfiniteSlider } from '@/components/motion-primitives/infinite-slider'
import { ProgressiveBlur } from '@/components/motion-primitives/progressive-blur'
import { FeaturesSection } from '@/components/features-section'
import { BentoFeaturesSection } from '@/components/bento-features-section'
import { TestimonialSection } from '@/components/testimonial-section'
import { IndicatorsSection } from '@/components/indicators-section'
import { StatsSection } from '@/components/stats-section'
import { FeatureTabsSection } from '@/components/feature-tabs-section'
import { SupportResistanceSection } from '@/components/support-resistance-section'
import { SignalsFeedSection } from '@/components/signals-feed-section'
import { CopilotSection } from '@/components/copilot-section'
import { PwaSection } from '@/components/pwa-section'
import { MacbookScrollSection } from '@/components/macbook-scroll'
import { FaqSection } from '@/components/faq-section'
import { PixelHeading } from '@/components/pixel-heading'
import { PlaneLoader } from '@/components/ui/plane-loader'
import { IntercomProvider } from '@/components/intercom-provider'

export default function LandingPage() {
  const { authenticated, ready } = usePrivy()
  const router = useRouter()
  const revealRef = useRef<HTMLImageElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [privyTimedOut, setPrivyTimedOut] = useState(false)

  useEffect(() => {
    document.documentElement.classList.add("hide-scrollbar")
    return () => document.documentElement.classList.remove("hide-scrollbar")
  }, [])

  useEffect(() => {
    setMounted(true)
    const t = setTimeout(() => setPrivyTimedOut(true), 3000)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (mounted && ready && authenticated) {
      router.replace('/overview')
    }
  }, [mounted, ready, authenticated, router])

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Show plane loader only until Privy has resolved auth state (3s max)
  if (!mounted || (!ready && !privyTimedOut)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <PlaneLoader />
      </div>
    )
  }

  // Authenticated users are being redirected by the effect above — render nothing while in-flight
  if (authenticated) return null

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const el = revealRef.current
    if (el) {
      el.style.setProperty('--mx', `${x}px`)
      // matches reference: offset by half container height so image centres on cursor
      el.style.setProperty('--my', `${y + rect.height * 0.5}px`)
    }
  }

  const handleMouseLeave = () => {
    const el = revealRef.current
    if (el) {
      el.style.setProperty('--mx', '-9999px')
      el.style.setProperty('--my', '-9999px')
    }
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'var(--font-ppmori), sans-serif' }}>
      <IntercomProvider hideLauncher={false} />

      {/* Nav */}
      <header className="absolute top-0 z-20 w-full" role="banner">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link href="/" className="flex items-center">
            <Logo className="h-4 w-auto text-white" />
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="lg" className="text-white/60 hover:text-white border-transparent hover:bg-white/8">
              <Link href="/auth/signin">Sign in</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white/20 text-white bg-transparent hover:bg-white/10 hover:border-white/40">
              <Link href="/auth/signup">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero — mouse tracked on the whole section */}
      <section
        aria-label="Hero"
        className="relative overflow-hidden pt-24"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Layer 1 (bottom): LaserFlow — h-screen on mobile so beam reaches preview, inset-0 on desktop (original) */}
        <div className="absolute inset-x-0 top-0 h-screen md:inset-0 md:h-auto" style={{ zIndex: 0 }}>
          <LaserFlow
            color="#ffb703"
            horizontalBeamOffset={0.1}
            verticalBeamOffset={isMobile ? -0.2 : 0.0}
            verticalSizing={isMobile ? 5 : 2}
            horizontalSizing={0.5}
            wispDensity={1}
            wispIntensity={5}
            wispSpeed={15}
            fogIntensity={0.45}
            fogScale={0.3}
            flowSpeed={0.35}
            flowStrength={0.25}
            decay={1.1}
            falloffStart={1.2}
            fogFallSpeed={0.6}
            mouseTiltStrength={0.01}
          />
        </div>

        {/* Layer 2: Reveal image — behind the laser beam, tracked in section space */}
        <img
          ref={revealRef}
          src="/previews/dashboard-preview.webp"
          alt=""
          aria-hidden

          style={{
            position: 'absolute',
            width: '100%',
            top: '-50%',
            left: 0,
            zIndex: 1,
            mixBlendMode: 'lighten',
            opacity: 0.3,
            pointerEvents: 'none',
            ['--mx' as string]: '-9999px',
            ['--my' as string]: '-9999px',
            WebkitMaskImage:
              'radial-gradient(circle at var(--mx) var(--my), rgba(255,255,255,1) 0px, rgba(255,255,255,0.95) 60px, rgba(255,255,255,0.6) 120px, rgba(255,255,255,0.25) 180px, rgba(255,255,255,0) 240px)',
            maskImage:
              'radial-gradient(circle at var(--mx) var(--my), rgba(255,255,255,1) 0px, rgba(255,255,255,0.95) 60px, rgba(255,255,255,0.6) 120px, rgba(255,255,255,0.25) 180px, rgba(255,255,255,0) 240px)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
          }}
        />

        {/* Layer 3: Hero text — above laser */}
        <div className="relative mx-auto max-w-5xl px-6" style={{ zIndex: 10 }}>
          <div className="sm:mx-auto lg:mr-auto lg:mt-0">
            <h1 className="mt-8 max-w-2xl text-balance text-5xl font-medium md:text-6xl lg:mt-16">
              Your Crypto Trading Copilot.
            </h1>
            <p className="mt-8 max-w-xl text-pretty text-lg text-white/50">
              Algoglide scans 400+ Binance futures pairs around the clock. Every setup is filtered by Algo before it hits your feed — and pushed to your phone the second it fires.
            </p>
            <div className="mt-12 flex items-center gap-3">
              <Button asChild size="lg" className="bg-white text-black border-white hover:bg-white/90 px-5 text-sm">
                <Link href="/auth/signup">Start Now</Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="text-white/60 hover:text-white border-transparent hover:bg-white/8 px-5 text-sm">
                <Link href="/overview">
                  View dashboard
                  <ChevronRight className="ml-1 size-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Layer 4: Dashboard card — on top of laser and reveal */}
        <div className="relative -mr-56 mt-8 overflow-hidden sm:mr-0 sm:mt-12 md:mt-20" style={{ zIndex: 6 }}>
          <div
            className="relative mx-auto max-w-[1100px] overflow-hidden bg-black"
            style={{ maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)' }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/previews/dashboard-preview.webp"
              alt="Algoglide dashboard"
              className="relative w-full object-top"
            />
          </div>
        </div>
      </section>

      {/* Logo cloud */}
      <section aria-label="Supported by" className="overflow-hidden py-16 border-t border-white/5">
        <div className="group relative mx-auto max-w-7xl px-6">
          <div className="flex flex-col items-center md:flex-row">
            <div className="md:max-w-44 md:border-r md:border-white/10 md:pr-6">
              <p className="text-end text-sm text-white/40">Powered by</p>
            </div>
            <div className="relative py-6 md:w-[calc(100%-11rem)]">
              <InfiniteSlider speedOnHover={20} speed={30} gap={80}>
                <img src="/supporters/coinmarketcap.svg" alt="CoinMarketCap" className="h-12 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/tradingview.svg" alt="TradingView" className="h-6 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/cvvc.svg" alt="CVVC" className="h-6 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/circle.svg" alt="Circle" className="h-6 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/coinmarketcap.svg" alt="CoinMarketCap" className="h-12 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/tradingview.svg" alt="TradingView" className="h-6 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/cvvc.svg" alt="CVVC" className="h-6 w-auto opacity-50 brightness-[10]" />
                <img src="/supporters/circle.svg" alt="Circle" className="h-6 w-auto opacity-50 brightness-[10]" />
              </InfiniteSlider>

              <div aria-hidden className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-black" />
              <div aria-hidden className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-black" />
              <ProgressiveBlur className="pointer-events-none absolute left-0 top-0 h-full w-20" direction="left" blurIntensity={1} />
              <ProgressiveBlur className="pointer-events-none absolute right-0 top-0 h-full w-20" direction="right" blurIntensity={1} />
            </div>
          </div>
        </div>
      </section>

      <FeaturesSection />
      {/* <BentoFeaturesSection /> */}
      <TestimonialSection />
      <IndicatorsSection />
      <StatsSection />
      <FeatureTabsSection />
      <SupportResistanceSection />
      <CopilotSection />
      <SignalsFeedSection />
      <PwaSection />
      {/* <MacbookScrollSection /> */}
      <FaqSection />

      {/* Footer */}
      <footer role="contentinfo" className="w-full bg-black border-t border-white/5 pt-20 pb-6 overflow-hidden">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-6 pb-10 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-white/25 text-center sm:text-left">&copy; {new Date().getFullYear()} Algoglide. All rights reserved.</p>
          <div className="flex gap-6 text-xs text-white/25">
            <Link href="/privacy" className="hover:text-white/50 transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-white/50 transition-colors">Terms of Service</Link>
            <a href="mailto:support@algoglide.com" className="hover:text-white/50 transition-colors">Contact</a>
          </div>
        </div>
        <div className="flex justify-center">
        <PixelHeading
          mode="wave"
          autoPlay
          cycleInterval={120}
          staggerDelay={35}
          showLabel={false}
          className="text-[18.5vw] leading-none tracking-tighter whitespace-nowrap"
          style={{ color: '#ffb703' }}
        >
          algoglide
        </PixelHeading>
        </div>
      </footer>
    </div>
  )
}
